import React, { useEffect, useRef, useState } from 'react'
import { getUpgradeProgress, subscribe as subscribeTextureQuality } from '../lib/textureQuality'

// How long the "textures updated" completion state stays visible after an
// upgrade finishes. Fast upgrades (small KTX2s on a warm connection) complete
// in well under a second — without a settle beat the pill flashes "0/16" and
// vanishes, which reads as a glitch instead of progress (user feedback,
// ENG-757).
const DONE_HOLD_MS = 1600

/**
 * TextureUpgradeIndicator — small viewport pill shown while the texture tier
 * cascade is downloading higher-resolution variants (ENG-757). Reads the
 * textureQuality module singleton (no polling, subscription tick only).
 * Two states: active ("textures 3/16") and a brief completion beat
 * ("textures updated") so even instant upgrades communicate what happened.
 * pointer-events none so it never blocks the scene.
 */
export default function TextureUpgradeIndicator() {
  const [progress, setProgress] = useState(getUpgradeProgress())
  const [justDone, setJustDone] = useState(false)
  const wasActiveRef = useRef(false)
  const doneTimerRef = useRef(null)

  useEffect(() => {
    const unsub = subscribeTextureQuality(() => {
      const p = getUpgradeProgress()
      setProgress(p)
      if (p.active) {
        wasActiveRef.current = true
        if (doneTimerRef.current) { clearTimeout(doneTimerRef.current); doneTimerRef.current = null }
        setJustDone(false)
      } else if (wasActiveRef.current) {
        // Upgrade just finished — hold a completion beat instead of blinking out.
        wasActiveRef.current = false
        setJustDone(true)
        doneTimerRef.current = setTimeout(() => { setJustDone(false); doneTimerRef.current = null }, DONE_HOLD_MS)
      }
    })
    return () => { unsub(); if (doneTimerRef.current) clearTimeout(doneTimerRef.current) }
  }, [])

  if (!progress.active && !justDone) return null
  const done = !progress.active
  return (
    <div style={{
      position: 'absolute', top: 12, right: 12, zIndex: 30,
      background: 'rgba(20,20,20,0.85)', border: `1px solid ${done ? '#3a5a3a' : '#333'}`,
      borderRadius: 4, padding: '4px 10px',
      color: '#9ec45c', fontFamily: 'var(--m-font)', fontSize: 11,
      pointerEvents: 'none',
      transition: 'opacity 300ms',
    }} data-testid="texture-upgrade-indicator">
      {done ? 'textures updated' : `textures ${progress.done}/${progress.total}`}
    </div>
  )
}
